import { Injectable } from '@angular/core';
import { AppService } from '../shared/app.service';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { state2 } from '../shared/state';
import { IZittingDb, IAgendapuntDb, IDocumentDb } from '../shared/offline/cobradb';
import { mergeMap, map } from 'rxjs/operators';


export interface IDocument {
  Id: number;
  Titel: string;
  Verwijzing: string;
}
export interface IAgendapunt extends IAgendapuntDb {
  GroupLevel?: number;
  GroupValue?: string;
  Groups?: IGroup[];
}

export interface ISubpunt {
  Id: number;
  Volgnummer: string;
  Titel: string;
  StandpuntAanvaard: number;
  StandpuntBespreken: number;
  StandpuntOnbepaald: number;
  StandpuntToegelaten: boolean;
}

export interface IDetailAgendapunt {
  Documenten?: IDocument[];
  Subpunten?: ISubpunt[];
  StandpuntAanvaard?: number;
  StandpuntBespreken?: number;
  StandpuntOnbepaald?: number;
  StandpuntToegelaten: boolean;
  OnbepaaldIsBespreken?: boolean;
  AantalNotities?: number;
  StreamingLink: string;
}
export const enum StandpuntStatus {
  Onbepaald = 0,
  Bespreken = 1,
  Aanvaard = 2
}

export interface IDetailZitting {
  zitting: IZittingDb; agendapunten: IAgendapuntDb[]; documenten: IDocumentDb[];
}
export interface IZittingResult {
  detail: IDetailZitting;
  groeperingsIds: number[];
}


@Injectable()
export class DetailZittingService {

  toggleBookmark(puntId: number, bladwijzerId: number) {
    // TODO misschien beter naar bookmarkservice ifv state
    const url = this.appService.getUrl(`api/Agendapunt/togglebookmark?puntId=${puntId}&bladwijzerId=${bladwijzerId}`);
    state2.remove('recentebladwijzers');
    return this.http.post<number | null>(url, {});
  }
  constructor(private appService: AppService, private http: HttpClient) { }

  getDetailZitting(zittingId: number, bespreken: boolean = false, refresh = false): Observable<IZittingResult> {
    if (refresh) { state2.removeSubKey('detailzittingdata', zittingId); }
    return state2.getSubValueOrDefault('detailzittingdata', zittingId, this.getDetailZittingInternal(zittingId)).pipe(
      mergeMap(detail =>
        this.getSoortZittingGroeperingIds(detail.zitting.SoortZittingId)
          .pipe(
            map(groeperingsIds => ({ detail, groeperingsIds, bespreken }))
          )
      )
    );
  }

  private getDocumentenWithExt(detailZitting: IDetailZitting) {
    detailZitting.documenten.forEach(d => d.Titel = this.appService.getTitelWithExtension(d.Titel, d.Verwijzing));
    return detailZitting;
  }

  private getDetailZittingInternal(zittingId: number) {
    const url = this.appService.getUrl(`api/zitting/getdetailzitting?zittingId=${zittingId}`);
    return this.http.post<IDetailZitting>(url, {}).pipe(
      map(detailZitting => this.getDocumentenWithExt(detailZitting))
    );
  }
  private getSoortZittingGroeperingIds(soortZittingid: number) {
    const url = this.appService.getUrl(`api/zitting/getsoortzittinggroeperingids?soortZittingId=${soortZittingid}`);
    return state2.getSubValueOrDefault('soortzittinggroeperingids', soortZittingid, this.http.post<number[]>(url, {}));
  }
  private setDocumentTitelWithExt(detailAgendapunt: IDetailAgendapunt) {
    detailAgendapunt.Documenten.forEach(d => d.Titel = this.appService.getTitelWithExtension(d.Titel, d.Verwijzing));
    return detailAgendapunt;
  }

  getDetailPunt(punt: IAgendapunt): Observable<IDetailAgendapunt> {
    if (punt == null) { return of(null as IDetailAgendapunt); }
    const url = this.appService.getUrl(`api/agendapunt/getdetailagendapunt?agendapuntId=${punt.Id}`);
    return this.http.post<IDetailAgendapunt>(url, {}).pipe(
      map(detailAgendapunt => this.setDocumentTitelWithExt(detailAgendapunt))
    );
  }
  toggleZittingAanwezig(zittingId: number) {
    const url = this.appService.getUrl('api/zitting/togglezittingaanwezig');
    return this.http.post<boolean>(url, {zittingId});
  }
  notitiesForZiting$(zittingId:number){
    const url = this.appService.getUrl('api/notitie/getnotitieszitting');
    return this.http.post<IAtHomePersoonNotitieZitting[]>(url, {zittingId});
  }
}

export interface IAtHomePersoonNotitieZitting {
  PuntId:number,
  NotitieId:number,
  VolgnummerPunt:string,
  TitelPunt:string, 
  NotitieInhoud:string, 
  EigenaarNotitie:string
}

export interface IGroup {
  level: number;
  value: string;
}

export class GroeperenService {
  groupByValues: string[];
  public groups: IGroup[];
  maxLevelGroup: number;
  constructor(private groupColumnIds: number[]) {
    this.groups = [];
  }
  initialize() {
    this.groupByValues = this.groupColumnIds.map(() => '');
    this.maxLevelGroup = this.groupColumnIds.length;
  }
  isBreak(punt: IAgendapunt) {
    if (this.maxLevelGroup === 0) { return -1; }
    this.groups = [];
    let currentLevel = 0;
    for (const groupColumnId of this.groupColumnIds) {
      const value = this.getValueFromPunt(punt, groupColumnId);
      if (value && this.groupByValues[currentLevel] !== value) {
        this.bepaalGewijzigdeGroeperingsWaarden(punt, currentLevel);
        return currentLevel;
      }
      currentLevel++;
    }
    return -1;
  }
  bepaalGewijzigdeGroeperingsWaarden(punt: IAgendapunt, currentLevel: number) {
    for (let niveau = currentLevel; niveau < this.maxLevelGroup; niveau++) {
      this.groupByValues[niveau] = this.getValueFromPunt(punt, this.groupColumnIds[niveau]);
      this.groups.push({ level: niveau, value: this.groupByValues[niveau] });
    }
  }
  getValueFromPunt(punt: IAgendapunt, groupColumnId: number): string {
    switch (groupColumnId) {
      case 1: return punt.Classificatie;
      case 2: return punt.Indeling;
      case 3: return punt.SoortPunt;
      case 4: return punt.BevoegdPersoon || '';
      case 5: return punt.Dienst;
      case 6: return punt.Hoofdrubriek || '';
      case 7: return punt.Nevenrubriek || '';
      default: throw new Error('Ongeldige kolom groeperen ' + groupColumnId);
    }
  }
  groupValueList(level: number) {
    return this.groupByValues.filter((value, index) => index <= level).join(' >> ');
  }
}
